import { useQuery } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';

import { offerQueryKeys } from './useGetOffer';
import type { OpportunityRoom, OpportunityRoomAsset } from './useGetOffer';

export type OpportunityRelatedExtraGuestSurcharge = {
  age_range__c?: string | null;
  archive__c?: boolean | null;
  bedding_type__c?: string | null;
  collected_by__c?: string | null;
  cost_price__c?: number | string | null;
  cost_price_currency?: string | null;
  currency?: string | null;
  converted_cost__c?: number | string | null;
  extra_night_margin_check__c?: string | null;
  extra_guest_type__c?: string | null;
  id: string;
  inclusions__c?: string | null;
  margin__c?: number | string | null;
  name?: string | null;
  opportunity__c?: string | null;
  other_extra_bedding_type__c?: string | null;
  sell_price_aud__c?: number | string | null;
  synced_at?: string | null;
};

export type OpportunityRelatedDeal = OpportunityRoom & {
  bundled_experiences__c?: string | null;
  channel_fare_type__c?: string | null;
  child_age_permitted__c?: string | null;
  number_of_bundled_experience__c?: number | string | null;
  offer_discount__c?: number | string | null;
  travel_end_date__c?: string | null;
  travel_start_date__c?: string | null;
};

export type OpportunityRelatedBundledExperience = {
  ancillary_deal_option__c?: string | null;
  bundled_experience_opportunity_book_by__c?: string | null;
  bundled_experience_opportunity_travel_to__c?: string | null;
  channel_fare_type__c?: string | null;
  converted_cost_aud__c?: number | string | null;
  currencyisocode?: string | null;
  deal_option__c?: string | null;
  dealoption__account_name?: string | null;
  dealoption__currencyisocode?: string | null;
  dealoption__deal_sub_category?: string | null;
  dealoption__destination?: string | null;
  dealoption__fare_type_display?: string | null;
  dealoption__name?: string | null;
  dealoption__show_in?: string | null;
  dealoption__value_cost_currency?: number | string | null;
  fare_type_display__c?: string | null;
  id: string;
  inactive__c?: boolean | null;
  is_bundled_experience_ending_before_opp__c?: boolean | null;
  name?: string | null;
  opportunityid__c?: string | null;
  rrp__c?: number | string | null;
  sell_price_uplift__c?: number | string | null;
  sell_price_uplift_number__c?: number | string | null;
  synced_at?: string | null;
  tag__c?: string | null;
  view_in_led__c?: string | null;
};

export type OpportunityRelatedInclusion = {
  applicable_length_of_stay__c?: string | null;
  applicable_to__c?: string | null;
  applicable_to_which_partner__c?: string | null;
  applies__c?: string | null;
  asset?: OpportunityRoomAsset | null;
  asset__c?: string | null;
  asset__name?: string | null;
  createdby__name?: string | null;
  createdbyid?: string | null;
  createddate?: string | null;
  currencyisocode?: string | null;
  deal_offer__c?: string | null;
  deal_option_name__c?: string | null;
  description__c?: string | null;
  id: string;
  included_for_extra_guest__c?: string | null;
  inclusion_description__c?: string | null;
  inclusions_total_value__c?: number | string | null;
  key__c?: string | null;
  lastmodifiedby__name?: string | null;
  lastmodifiedbyid?: string | null;
  lastmodifieddate?: string | null;
  name?: string | null;
  rate_type__c?: string | null;
  rrp__c?: number | string | null;
  synced_at?: string | null;
  total_value__c?: number | string | null;
  type__c?: string | null;
  valid_for__c?: string | null;
  venues__c?: string | null;
};

export type OpportunityRelatedRecords = {
  bundled_experiences?: Array<OpportunityRelatedBundledExperience>;
  deals?: Array<OpportunityRelatedDeal>;
  extra_guest_surcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
  inclusions?: Array<OpportunityRelatedInclusion>;
  rooms?: Array<OpportunityRoom>;
};

export function useGetOpportunityRelated(
  opportunityId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: offerQueryKeys.opportunityRelated(opportunityId),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getOpportunityRelated(opportunityId);
      return response.result as OpportunityRelatedRecords;
    },
    enabled: enabled && !!opportunityId,
  });
}
